import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { FaFilter, FaTimes } from 'react-icons/fa';

const TarifariosFilters = ({
    show,
    onHide,
    onFiltersChange,
    initialFilters = {},
    tipoTarifario,
    // Opciones para dropdowns
    regiones,
    comunas,
    servicios,
    clientes,
    tms,
    // Handlers para cambios que afectan otros dropdowns
    onRegionChange
}) => {
    // Estados para filtros
    const [filtros, setFiltros] = useState({
        regionId: initialFilters.regionId || '',
        comunaId: initialFilters.comunaId || '',
        servicioId: initialFilters.servicioId || '',
        clienteId: initialFilters.clienteId || '',
        tmsId: initialFilters.tmsId || ''
    });

    // Actualizar filtros cuando cambien los initialFilters
    useEffect(() => {
        setFiltros({
            regionId: initialFilters.regionId || '',
            comunaId: initialFilters.comunaId || '',
            servicioId: initialFilters.servicioId || '',
            clienteId: initialFilters.clienteId || '',
            tmsId: initialFilters.tmsId || ''
        });
    }, [initialFilters]);

    // Handlers para cambios en filtros
    const handleRegionChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            regionId: value,
            comunaId: '' // Limpiar comuna cuando cambia región
        }));
        // Notificar cambio para actualizar comunas disponibles
        if (onRegionChange) {
            onRegionChange(value);
        }
    };

    const handleComunaChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            comunaId: value
        }));
    };

    const handleServicioChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            servicioId: value
        }));
    };

    const handleClienteChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            clienteId: value
        }));
    };

    const handleTmsChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            tmsId: value
        }));
    };

    // Función para aplicar filtros
    const handleAplicarFiltros = () => {
        onFiltersChange(filtros);
        onHide();
    };

    // Función para limpiar filtros
    const limpiarFiltros = () => {
        const filtrosVacios = {
            regionId: '',
            comunaId: '',
            servicioId: '',
            clienteId: '',
            tmsId: ''
        };
        setFiltros(filtrosVacios);
        onFiltersChange(filtrosVacios);
        onHide();
    };

    // Opciones para dropdowns
    const renderRegionOptions = () => [
        <option key="" value="">Todas las regiones</option>,
        ...regiones.map(region => (
            <option key={region.id} value={region.id}>
                {region.region}
            </option>
        ))
    ];

    const renderComunaOptions = () => [
        <option key="" value="">Todas las comunas</option>,
        ...comunas.map(comuna => (
            <option key={comuna.id} value={comuna.id}>
                {comuna.comuna}
            </option>
        ))
    ];

    const renderServicioOptions = () => [
        <option key="" value="">Todos los servicios</option>,
        ...servicios.map(servicio => (
            <option key={servicio.id} value={servicio.id}>
                {servicio.servicio}
            </option>
        ))
    ];

    const renderClienteOptions = () => [
        <option key="" value="">Todos los clientes</option>,
        ...clientes.map(cliente => (
            <option key={cliente.id} value={cliente.id}>
                {cliente.cliente}
            </option>
        ))
    ];

    const renderTmsOptions = () => [
        <option key="" value="">Todos los TMS</option>,
        ...tms.map(tm => (
            <option key={tm.id} value={tm.id}>
                {tm.tms}
            </option>
        ))
    ];

    // Determinar si cliente está habilitado según el tipo de tarifario
    const isClienteEnabled = tipoTarifario === 'ingreso';

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaFilter className="me-2" />
                    Filtros de Tarifarios
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Región</Form.Label>
                            <Form.Select
                                value={filtros.regionId}
                                onChange={(e) => handleRegionChange(e.target.value)}
                            >
                                {renderRegionOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Comuna</Form.Label>
                            <Form.Select
                                value={filtros.comunaId}
                                onChange={(e) => handleComunaChange(e.target.value)}
                            >
                                {renderComunaOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Servicio Spread</Form.Label>
                            <Form.Select
                                value={filtros.servicioId}
                                onChange={(e) => handleServicioChange(e.target.value)}
                            >
                                {renderServicioOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>TMS</Form.Label>
                            <Form.Select
                                value={filtros.tmsId}
                                onChange={(e) => handleTmsChange(e.target.value)}
                            >
                                {renderTmsOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                {/* Campo Cliente solo para tipo egreso */}
                {isClienteEnabled && (
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Cliente</Form.Label>
                                <Form.Select
                                    value={filtros.clienteId}
                                    onChange={(e) => handleClienteChange(e.target.value)}
                                >
                                    {renderClienteOptions()}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {/* Información adicional */}
                <div className="bg-light p-3 rounded">
                    <small className="text-muted">
                        <strong>Tip:</strong> Deje los campos vacíos para mostrar todos los resultados de esa categoría.
                        {!isClienteEnabled && (
                            <><br /><strong>Nota:</strong> El filtro de cliente solo está disponible para tarifarios de tipo "Egreso".</>
                        )}
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={limpiarFiltros}>
                    <FaTimes className="me-1" />
                    Limpiar Filtros
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleAplicarFiltros}>
                    <FaFilter className="me-1" />
                    Aplicar Filtros
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

TarifariosFilters.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onFiltersChange: PropTypes.func.isRequired,
    initialFilters: PropTypes.object,
    tipoTarifario: PropTypes.string,
    regiones: PropTypes.array,
    comunas: PropTypes.array,
    servicios: PropTypes.array,
    clientes: PropTypes.array,
    tms: PropTypes.array,
    onRegionChange: PropTypes.func
};

TarifariosFilters.defaultProps = {
    initialFilters: {},
    tipoTarifario: 'ingreso',
    regiones: [],
    comunas: [],
    servicios: [],
    clientes: [],
    tms: [],
    onRegionChange: null
};

export default TarifariosFilters;
